import { isInlineAudioShape } from '@common/media-layer/utils/voice-note-shape'

import type { IMessage } from '../types/conversation.types'

/** Stable playback key (must match ChatMessageBubble). Optimistic rows use tempId / uniqueRefId. */
export function getChatVoicePlaybackKey(message: IMessage): string {
  const withTemp = message as IMessage & { tempId?: string; uniqueRefId?: string }
  return withTemp.tempId || withTemp.uniqueRefId || message._id
}

/** Inline audio only — text/images/files break the autoplay chain. */
export function isChatAutoplayAudioMessage(message: IMessage | null | undefined): boolean {
  if (!message?.content) return false
  return isInlineAudioShape(message.content.media)
}

/** Immediate next newer message only; non-audio gaps stop autoplay. */
export function getNextChatVoicePlaybackKey(messages: readonly IMessage[], startIndex: number): string | null {
  const nextIndex = startIndex - 1
  if (nextIndex < 0) return null

  const nextMessage = messages[nextIndex]
  if (!isChatAutoplayAudioMessage(nextMessage)) return null

  return getChatVoicePlaybackKey(nextMessage)
}

/** Lazy resolver factory — reads messages when autoplay advances, not at render time. */
export function createResolveNextChatVoiceAutoplayKey(getMessages: () => readonly IMessage[], message: IMessage): () => string | null {
  const playbackKey = getChatVoicePlaybackKey(message)

  return () => {
    const messages = getMessages()
    const startIndex = messages.findIndex(item => getChatVoicePlaybackKey(item) === playbackKey)
    if (startIndex < 0) return null
    return getNextChatVoicePlaybackKey(messages, startIndex)
  }
}
